import React from 'react';
import Box from '../../components/Common/Box';

interface AlertProps {
    alerts: any[];
    stocks: any[];
    loading: boolean;
    saving: boolean;
    error: string;
    selectedStock: string;
    setSelectedStock: (v: string) => void;
    alertType: 'PRICE_ABOVE' | 'PRICE_BELOW' | 'PERCENT_CHANGE';
    setAlertType: (v: 'PRICE_ABOVE' | 'PRICE_BELOW' | 'PERCENT_CHANGE') => void;
    thresholdValue: string;
    setThresholdValue: (v: string) => void;
    handleCreateAlert: (e: React.FormEvent) => void;
    handleDeleteAlert: (id: number) => void;
    handleToggleActive: (alert: any) => void;
    getAlertTypeLabel: (type: string) => string;
    formatThreshold: (type: string, value: number) => string;
}

const DesktopAlertSettings: React.FC<AlertProps> = ({
    alerts, stocks, loading, saving, error,
    selectedStock, setSelectedStock, alertType, setAlertType,
    thresholdValue, setThresholdValue, handleCreateAlert,
    handleDeleteAlert, handleToggleActive, getAlertTypeLabel, formatThreshold
}) => {
    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
            {/* Create Alert Form */}
            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>➕ Créer une nouvelle alerte</p>
                </div>
                <div className='box-content box-horizontal-padding' style={{ padding: '20px' }}>
                    {error && (
                        <div style={{ padding: '10px', marginBottom: '15px', backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '6px', fontSize: '14px' }}>
                            {error}
                        </div>
                    )}
                    <form onSubmit={handleCreateAlert}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Société</label>
                                <select value={selectedStock} onChange={(e) => setSelectedStock(e.target.value)} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                                    {stocks.map((stock: any) => (
                                        <option key={stock.symbole} value={stock.symbole}>{stock.symbole} - {stock.nom_societe}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Type d'alerte</label>
                                <select value={alertType} onChange={(e) => setAlertType(e.target.value as any)} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '14px' }}>
                                    <option value="PRICE_ABOVE">📈 Prix supérieur à</option>
                                    <option value="PRICE_BELOW">📉 Prix inférieur à</option>
                                    <option value="PERCENT_CHANGE">📊 Variation supérieure à (%)</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                                    {alertType === 'PERCENT_CHANGE' ? 'Seuil (%)' : 'Prix seuil (FCFA)'}
                                </label>
                                <input type="number" value={thresholdValue} onChange={(e) => setThresholdValue(e.target.value)} placeholder={alertType === 'PERCENT_CHANGE' ? 'Ex: 5' : 'Ex: 15000'} step={alertType === 'PERCENT_CHANGE' ? '0.5' : '100'} min="0" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '14px' }} required />
                            </div>
                        </div>
                        <button type="submit" disabled={saving || !thresholdValue} className="button button-purple" style={{ padding: '12px 24px' }}>
                            {saving ? 'Création...' : '🔔 Créer l\'alerte'}
                        </button>
                    </form>
                </div>
            </Box>

            {/* Active Alerts List */}
            <Box>
                <div className='box-title box-vertical-padding box-horizontal-padding'>
                    <p>📋 Mes alertes ({alerts.length})</p>
                </div>
                <div className='box-content'>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Chargement des alertes...</div>
                    ) : alerts.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                            <p style={{ fontSize: '48px', marginBottom: '10px' }}>🔕</p>
                            <p>Aucune alerte configurée</p>
                            <p style={{ fontSize: '14px', marginTop: '10px' }}>Créez une alerte ci-dessus pour être notifié des variations de prix</p>
                        </div>
                    ) : (
                        <table className='data-table'>
                            <thead>
                                <tr>
                                    <th className='left'>Société</th>
                                    <th className='left'>Condition</th>
                                    <th className='center'>Statut</th>
                                    <th className='center'>Actif</th>
                                    <th className='right'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {alerts.map((alert: any) => (
                                    <tr key={alert.id} style={{ opacity: alert.is_triggered ? 0.6 : 1 }}>
                                        <td><strong>{alert.stock_symbol}</strong></td>
                                        <td>{getAlertTypeLabel(alert.alert_type)} {formatThreshold(alert.alert_type, alert.threshold_value)}</td>
                                        <td className='center'>
                                            {alert.is_triggered ? (
                                                <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' }}>✅ Déclenché</span>
                                            ) : (
                                                <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 12px', borderRadius: '20px', fontSize: '12px' }}>⏳ En attente</span>
                                            )}
                                        </td>
                                        <td className='center'>
                                            <label style={{ cursor: 'pointer' }}>
                                                <input type="checkbox" checked={alert.is_active} onChange={() => handleToggleActive(alert)} disabled={alert.is_triggered} style={{ width: '18px', height: '18px' }} />
                                            </label>
                                        </td>
                                        <td className='right'>
                                            <button onClick={() => handleDeleteAlert(alert.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px' }} title="Supprimer">🗑️</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </Box>

            {/* Info Box */}
            <Box>
                <div className='box-content' style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                        <span style={{ fontSize: '32px' }}>💡</span>
                        <div>
                            <h4 style={{ margin: '0 0 10px 0' }}>Comment fonctionnent les alertes ?</h4>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#666', lineHeight: '1.8' }}>
                                <li><strong>Prix supérieur/inférieur</strong>: Alerte déclenchée quand le cours atteint le seuil défini</li>
                                <li><strong>Variation %</strong>: Alerte déclenchée quand la variation journalière dépasse le seuil</li>
                                <li>Les alertes sont vérifiées quotidiennement après la clôture du marché (18h30)</li>
                                <li>Une fois déclenchée, une notification email et in-app vous est envoyée</li>
                                <li>Les alertes déclenchées sont automatiquement désactivées</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </Box>
        </div>
    );
};

export default DesktopAlertSettings;
